import type { BesideField, BesideSource, MapPictureSource, PictureView } from "../map-picture/pictures.ts";

/**
 * The minimap's ground beside the map (ADR-147 decision 6; the author, 2026-11-01: "The minimap needs to also be seamless
 * enough to see ahead into the new/next map"): one picture source per placed neighbour, drawn from the neighbour chunks
 * the page already holds, so a field shows the neighbour's ground at the same offset as the world view and fetches
 * nothing of its own. A chunk that lands or is let go has its part of every picture drawn again. Marks and players
 * beside the map come with the edge view's entities (P1.4-T192); ground is all this draws.
 */

/**
 * One neighbour's pictures and where the neighbour lies in the owning map's tiles.
 */
export interface BesidePictures {
  readonly x: number;
  readonly y: number;
  readonly pictures: BesidePictureSource;
}

/**
 * What a neighbour's pictures do beyond a field's asks: compose, and draw a chunk's part again.
 */
export interface BesidePictureSource {
  /**
   * The picture of a slot about a position in the neighbour's tiles (`MapPictureSource.view`).
   *
   * @param slot The consumer's slot.
   * @param scale Pixels a tile.
   * @param x The position across, in the neighbour's tiles.
   * @param y The position down.
   * @param fieldWidth The field's width in pixels.
   * @param fieldHeight The field's height in pixels.
   * @returns The picture, or null.
   */
  view(slot: string, scale: number, x: number, y: number, fieldWidth: number, fieldHeight: number): PictureView | null;
  /**
   * Frees a slot's picture.
   *
   * @param slot The consumer's slot.
   */
  release(slot: string): void;
  /**
   * Composes owed chunks about a position in the neighbour's tiles.
   *
   * @param x The position across.
   * @param y The position down.
   * @returns True when a chunk was composed.
   */
  step(x: number, y: number): boolean;
  /**
   * Draws again the chunks tile rectangles touch.
   *
   * @param rects The rectangles, `x0` and `y0` inclusive, `x1` and `y1` exclusive.
   */
  redraw(rects: readonly { readonly x0: number; readonly y0: number; readonly x1: number; readonly y1: number }[]): void;
}

/**
 * The neighbours' pictures a minimap field draws beside the map's own.
 */
export class BesideMinimap implements BesideSource {
  private readonly rect = [{ x0: 0, y0: 0, x1: 0, y1: 0 }];

  /**
   * @param besides The neighbours' pictures, in the order the ground holds the neighbours.
   * @param chunkSize Chunk size in tiles.
   */
  public constructor(
    private readonly besides: readonly BesidePictures[],
    private readonly chunkSize: number,
  ) {}

  /**
   * Fills a slot's neighbour pictures about the player, the player's position moved into each neighbour's tiles.
   *
   * @param slot The consumer's slot.
   * @param scale Pixels a tile.
   * @param x The player's position across, in the owning map's tiles.
   * @param y The player's position down.
   * @param fieldWidth The field's width in pixels.
   * @param fieldHeight The field's height in pixels.
   * @param out The records to fill, grown only the first time.
   * @returns How many records hold a neighbour.
   */
  public views(slot: string, scale: number, x: number, y: number, fieldWidth: number, fieldHeight: number, out: BesideField[]): number {
    for (let index = 0; index < this.besides.length; index += 1) {
      const beside = this.besides[index] as BesidePictures;
      const record = out[index] ?? (out[index] = { ground: null, x: 0, y: 0 });
      record.x = x - beside.x;
      record.y = y - beside.y;
      record.ground = beside.pictures.view(slot, scale, record.x, record.y, fieldWidth, fieldHeight);
    }
    return this.besides.length;
  }

  /**
   * Frees a slot's neighbour pictures.
   *
   * @param slot The consumer's slot.
   */
  public release(slot: string): void {
    for (let index = 0; index < this.besides.length; index += 1) {
      (this.besides[index] as BesidePictures).pictures.release(slot);
    }
  }

  /**
   * Composes the neighbours' owed chunks, the player's nearest first, each in its own tiles.
   *
   * @param x The player's position across, in the owning map's tiles.
   * @param y The player's position down.
   * @returns True when any chunk was composed.
   */
  public step(x: number, y: number): boolean {
    let composed = false;
    for (let index = 0; index < this.besides.length; index += 1) {
      const beside = this.besides[index] as BesidePictures;
      composed = beside.pictures.step(x - beside.x, y - beside.y) || composed;
    }
    return composed;
  }

  /**
   * Draws a neighbour chunk's part of its pictures again, for a chunk that landed or was let go.
   *
   * @param index The neighbour's index.
   * @param cx Chunk column in the neighbour's grid.
   * @param cy Chunk row.
   */
  public changed(index: number, cx: number, cy: number): void {
    const rect = this.rect[0] as { x0: number; y0: number; x1: number; y1: number };
    rect.x0 = cx * this.chunkSize;
    rect.y0 = cy * this.chunkSize;
    rect.x1 = rect.x0 + this.chunkSize;
    rect.y1 = rect.y0 + this.chunkSize;
    this.besides[index]?.pictures.redraw(this.rect);
  }

  /**
   * The owning map's pictures with this ground beside them: the map's own answer every ask, and composing steps both.
   *
   * @param own The owning map's pictures.
   * @param crossing True for the pictures a crossing's change of stage gives, which a field holds its last drawing for
   *   while they are bare (`MapPictureSource.crossing`).
   * @returns The source the minimap is given.
   */
  public around(own: MapPictureSource, crossing = false): MapPictureSource {
    return {
      crossing,
      view: (slot, scale, x, y, fieldWidth, fieldHeight) => own.view(slot, scale, x, y, fieldWidth, fieldHeight),
      release: (slot) => own.release(slot),
      owed: (slot) => own.owed?.(slot) ?? 0,
      step: (x, y) => {
        const besideComposed = this.step(x, y);
        return own.step(x, y) || besideComposed;
      },
      beside: this,
    };
  }
}
